import { useState } from 'react';
import { useStore, calcLineas, formatEur, today } from '../store';
import { Btn, Badge, Modal, Input, Select, StatCard, SearchBar, useSearch, useLocalStorage, LineasEditor, Confirm, ClienteSelector, MultiSelect, useSortable, SortableTh, EstadoQuick, useT, pastelBtn } from './UI';
import { printTicket } from '../utils/print';
import { exportTickets } from '../utils/exportExcel';

function termicaTicket(doc) {
  return fetch('/api/imprimir-termica', {
    method: 'POST',
    headers: { Authorization: `Bearer ${localStorage.getItem('ri_token')}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ tipo: 'ticket', rep: doc })
  }).then(r => r.json()).then(d => { if (!d.ok) throw new Error(d.error); });
}

function enviarWhatsappTicket(t, clientes) {
  const cli = clientes.find(c => String(c.id) === String(t.clienteId));
  const actual = t.clienteTel || cli?.telefono || '';
  const tel = window.prompt(`Enviar WhatsApp a ${t.clienteNombre || 'cliente'}:`, actual);
  if (!tel || !tel.trim()) return;
  let d = tel.trim().replace(/\D/g, '');
  if (d.startsWith('00')) d = d.slice(2);
  if (d.length === 9) d = '34' + d;
  fetch(`/api/whatsapp/documento/tickets/${t.id}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${localStorage.getItem('ri_token')}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ to: d }),
  }).then(r => r.json()).then(r => { if (r.ok) alert(`✅ Enviado por WhatsApp a ${t.clienteNombre || 'cliente'} (${tel.trim()})`); else alert('❌ ' + r.error); })
  .catch(e => alert('❌ ' + e.message));
}

const PAGOS   = ['Efectivo','Tarjeta','Bizum','Transferencia'];
const ESTADOS = ['Cobrado','Pendiente','Anulado'];

function statusBadge(e) {
  const m = { Cobrado:'green', Pendiente:'amber', Anulado:'red' };
  return <Badge variant={m[e]||'gray'}>{e}</Badge>;
}

function newTicket(cfg) {
  return { id:null, num:'', fecha: today(), clienteId:'', clienteNombre:'', pago:'Efectivo', lineas:[{ desc:'', cant:1, precio:0, iva: cfg.iva }], estado:'Cobrado' };
}

export default function Tickets() {
  const theme = useT();
  const { data, addItem, replaceItem, deleteItem, nextNum } = useStore();
  const { tickets, config, clientes, articulos } = data;
  const { q, setQ, filtered } = useSearch(tickets, ['num','clienteNombre','estado'], 'tickets');
  const { sorted: sortedTickets, sortField, sortDir, handleSort } = useSortable(filtered, 'num', 'desc');
  const [modal, setModal]     = useState(null);
  const [form, setForm]       = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [exportModal, setExportModal] = useState(false);
  const [filtroEstados, setFiltroEstados] = useLocalStorage('filtroEstados_tickets', []);
  const [expDesde, setExpDesde] = useState('');
  const [expHasta, setExpHasta] = useState('');

  const hoyStr      = today();
  const totHoy      = tickets.filter(t => t.fecha === hoyStr);
  const totMes      = tickets.filter(t => t.fecha.slice(0,7) === hoyStr.slice(0,7));
  const importeHoy  = totHoy.reduce((s,t) => s + calcLineas(t.lineas).total, 0);

  function openNew()    { setForm(newTicket(config)); setModal('form'); }
  function openEdit(t)  { setForm({...t, lineas: t.lineas.map(l=>({...l}))}); setModal('form'); }

  /* Guarda cliente nuevo en la lista si no existía */
  function handleNuevoCliente(nuevo) {
    addItem('clientes', nuevo);
  }

  function handleEditarCliente(cli) {
    replaceItem('clientes', cli);
  }

  function handleClienteChange({ id, nombre, nif, direccion, telefono }) {
    setForm(f => ({ ...f, clienteId: id, clienteNombre: nombre, clienteNif: nif||'', clienteDireccion: direccion||'', clienteTel: telefono||'' }));
  }

  function buildTicket() {
    if (!form.lineas.length || !form.lineas[0].desc) { alert('Añade al menos una línea.'); return null; }

    let clienteIdFinal = form.clienteId;
    if (form.clienteNombre && !form.clienteId) {
      const existe = clientes.find(c => c.nombre.toLowerCase() === form.clienteNombre.toLowerCase());
      if (!existe) {
        const nuevo = { id: Date.now(), nombre: form.clienteNombre, nif:'', telefono:'', email:'', direccion:'' };
        addItem('clientes', nuevo);
        clienteIdFinal = nuevo.id;
      } else {
        clienteIdFinal = existe.id;
      }
    }

    const formFinal = { ...form, clienteId: clienteIdFinal };
    if (form.id) {
      replaceItem('tickets', formFinal);
      return formFinal;
    } else {
      const n = config.sigTicket;
      const num = `T-${String(n).padStart(4,'0')}`;
      nextNum('sigTicket', 'T-');
      const doc = { ...formFinal, id: n, num };
      addItem('tickets', doc);
      return doc;
    }
  }

  function save() {
    if (buildTicket()) setModal(null);
  }

  const { base, iva, total } = form ? calcLineas(form.lineas) : { base:0, iva:0, total:0 };

  return (
    <div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:10, marginBottom:16 }}>
        <StatCard label="Tickets hoy"      value={totHoy.length} />
        <StatCard label="Tickets este mes" value={totMes.length} />
        <StatCard label="Total tickets"    value={tickets.length} />
        <StatCard label="Importe hoy"      value={formatEur(importeHoy)} color="#0e9f6e" />
      </div>

      <SearchBar value={q} onChange={setQ} placeholder="Buscar por número, cliente, estado...">
        <MultiSelect options={ESTADOS} selected={filtroEstados} onChange={setFiltroEstados} placeholder="Estado: Todos" />
        <Btn variant="ghost" title="Exportar listado a Excel" onClick={() => setExportModal(true)}>📊 Exportar Excel</Btn>
        <Btn variant="primary" onClick={openNew} title="Crear nuevo ticket de venta">+ Nuevo ticket</Btn>
      </SearchBar>

      <div style={{ background:theme.surface, borderRadius:12, border:`1px solid ${theme.border}`, overflowX:'auto' }}>
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
          <thead>
            <tr style={{ background:theme.surface2 }}>
              {[['Nº','num'],['Fecha','fecha'],['Cliente','clienteNombre'],['Artículos',null],['Total',null],['Pago','pago'],['Estado','estado'],[''  ,null]].map(([label,field]) => (
                <SortableTh key={label+field} label={label} field={field} sortField={sortField} sortDir={sortDir} onSort={handleSort} />
              ))}
            </tr>
          </thead>
          <tbody>
            {(filtroEstados.length ? sortedTickets.filter(t => filtroEstados.includes(t.estado)) : sortedTickets).map((t, i) => {
              const { total } = calcLineas(t.lineas);
              return (
                <tr key={t.id} style={{ borderBottom:`1px solid ${theme.border}`, background: i%2 ? theme.surface2 : 'transparent' }}>
                  <td style={{ padding:'10px 14px', fontWeight:600, color:'#1a56db' }}>{t.num}</td>
                  <td style={{ padding:'10px 14px' }}>{new Date(t.fecha).toLocaleDateString('es-ES')}</td>
                  <td style={{ padding:'10px 14px' }}>{t.clienteNombre || <span style={{color:theme.faint}}>Particular</span>}</td>
                  <td style={{ padding:'10px 14px', color:theme.body, maxWidth:180, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                    {t.lineas.map(l=>l.desc).filter(Boolean).join(', ')}
                  </td>
                  <td style={{ padding:'10px 14px', fontWeight:600 }}>{formatEur(total)}</td>
                  <td style={{ padding:'10px 14px' }}>{t.pago}</td>
                  <td style={{ padding:'10px 14px', position:'relative' }}>
                    <EstadoQuick estado={t.estado} estados={ESTADOS}
                      colores={{ Cobrado:'#dcfce7', Pendiente:'#fef3c7', Anulado:'#fee2e2' }}
                      textos={{ Cobrado:'#166534', Pendiente:'#92400e', Anulado:'#991b1b' }}
                      onChange={v => replaceItem('tickets', { ...t, estado: v })} />
                  </td>
                  <td style={{ padding:'10px 14px' }}>
                    <div style={{ display:'flex', gap:4 }}>
                      <Btn size="sm" variant="ghost" title="Editar ticket" onClick={() => openEdit(t)}>✏️</Btn>
                      <Btn size="sm" variant="ghost" title="Imprimir ticket térmico" onClick={() => termicaTicket(t).catch(e => { alert('Error impresora: '+e.message+'\n\nAbriendo ventana...'); printTicket(t, config); })}>🖨️</Btn>
                      <Btn size="sm" variant="ghost" style={{ ...pastelBtn(theme,'green') }} title="Enviar PDF por WhatsApp" onClick={() => enviarWhatsappTicket(t, clientes)}>💬 WA</Btn>
                      <Btn size="sm" variant="ghost" style={{ color:'#ef4444' }} title="Eliminar ticket" onClick={() => setConfirm(t.id)}>🗑️</Btn>
                    </div>
                  </td>
                </tr>
              );
            })}
            {!filtered.length && <tr><td colSpan={8} style={{ padding:30, textAlign:'center', color:theme.faint }}>No hay tickets</td></tr>}
          </tbody>
        </table>
      </div>

      {/* ── MODAL NUEVO / EDITAR ── */}
      {modal === 'form' && form && (
        <Modal title={form.id ? `Editar ${form.num}` : 'Nuevo ticket'} onClose={() => setModal(null)} width={700}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:14 }}>
            {form.id && <Input label="Nº Ticket" value={form.num} readOnly />}
            <Input label="Fecha" type="date" value={form.fecha} onChange={v => setForm(f=>({...f, fecha:v}))} />

            {/* Selector con botón nuevo cliente — ocupa toda la fila */}
            <div style={{ gridColumn:'1/-1' }}>
              <ClienteSelector
                clientes={clientes}
                clienteId={form.clienteId}
                clienteNombre={form.clienteNombre}
                onChange={handleClienteChange}
                onNuevoCliente={handleNuevoCliente}
                onEditarCliente={handleEditarCliente}
              />
            </div>

            <Select label="Forma de pago" value={form.pago}   onChange={v=>setForm(f=>({...f,pago:v}))}   options={PAGOS} />
            <Select label="Estado"        value={form.estado} onChange={v=>setForm(f=>({...f,estado:v}))} options={ESTADOS} />
          </div>

          <LineasEditor lineas={form.lineas} onChange={l=>setForm(f=>({...f,lineas:l}))} articulos={articulos} />

          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-end', marginTop:12, paddingTop:12, borderTop:`1px solid ${theme.border}` }}>
            <div style={{ fontSize:12, color:theme.muted }}>
              Base: <b>{formatEur(base)}</b> · IVA: <b>{formatEur(iva)}</b> ·{' '}
              <span style={{ fontSize:15, fontWeight:700, color:'#1a56db' }}>Total: {formatEur(total)}</span>
            </div>
            <div style={{ display:'flex', gap:8 }}>
              <Btn variant="outline" onClick={() => setModal(null)} title="Descartar cambios">Cancelar</Btn>
              <Btn variant="ghost" title="Guardar e imprimir en impresora térmica" onClick={() => { const doc = buildTicket(); if (doc) { setModal(null); setTimeout(() => termicaTicket(doc).catch(e => { alert('Error impresora: '+e.message+'\n\nAbriendo ventana...'); printTicket(doc, config); }), 200); } }}>🖨️ Guardar e imprimir</Btn>
              <Btn variant="primary" onClick={save} title="Guardar sin imprimir">Guardar</Btn>
            </div>
          </div>
        </Modal>
      )}

      {/* ── MODAL EXPORTAR ── */}
      {exportModal && (
        <Modal title="Exportar tickets a Excel" onClose={() => setExportModal(false)} width={420}>
          <p style={{ fontSize:13, color:theme.muted, marginBottom:16 }}>Rango de fechas. Vacío = todos los tickets.</p>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:16 }}>
            <Input label="Desde" type="date" value={expDesde} onChange={setExpDesde} />
            <Input label="Hasta" type="date" value={expHasta} onChange={setExpHasta} />
          </div>
          <div style={{ background:'#f0f9ff', borderRadius:8, padding:10, marginBottom:16, fontSize:12, color:'#0369a1' }}>
            {(() => {
              const n = tickets.filter(t => (!expDesde||t.fecha>=expDesde) && (!expHasta||t.fecha<=expHasta)).length;
              return `Se exportarán ${n} ticket${n!==1?'s':''}`;
            })()}
          </div>
          <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
            <Btn variant="outline"  onClick={() => setExportModal(false)}>Cancelar</Btn>
            <Btn variant="success"  onClick={() => { exportTickets(tickets, expDesde, expHasta, config); setExportModal(false); }}>📥 Descargar Excel</Btn>
          </div>
        </Modal>
      )}

      {confirm && <Confirm msg="¿Eliminar este ticket?" onOk={() => { deleteItem('tickets', confirm); setConfirm(null); }} onCancel={() => setConfirm(null)} />}
    </div>
  );
}
